import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { FREE_TRIAL_TOTAL, getFreeQuestionsUsed } from '../components/FreeTrialBanner.jsx';

export const PATHWAYS = {
    'medical-students': {
        slug: 'medical-students',
        title: 'Medical Student',
        subtitle: 'From first-year block exams to USMLE boards — built from your notes.',
        hero: 'Stop reading. Start generating questions from everything you already have.',
        stages: [
            {
                label: 'MS1 & MS2 — Class Prep',
                desc: "Paste lecture slides or notes and get mechanism-focused MCQs tailored to block exams. Reinforce pathophysiology, pharmacology, and anatomy in the format you'll actually see tested.",
            },
            {
                label: 'Shelf Exams',
                desc: 'Generate clinical vignette questions directly from rotation notes. Every shelf has a different flavor — FlashFonic matches the format to the rotation: IM, surgery, peds, ob/gyn, psych, neuro.',
            },
            {
                label: 'USMLE / COMLEX Step 1 & 2',
                desc: 'Board-format vignettes from your study materials. FlashFonic identifies weak topics from your accuracy data and rebuilds your question bank around your gaps.',
            },
        ],
        examTags: ['USMLE Step 1', 'USMLE Step 2 CK', 'COMLEX L1', 'COMLEX L2', 'Shelf Exams', 'Block Exams'],
        testimonial: {
            quote: "Generated an entire shelf exam Q-bank from my internal medicine rotation notes in one afternoon — it actually matched what my attending kept hammering on during rounds, not generic textbook stuff.",
            attribution: 'Priya Nair · MS3, Internal Medicine Shelf',
        },
    },
    'residents': {
        slug: 'residents',
        title: 'Resident / Fellow',
        subtitle: 'Step 3, rounds prep, and specialty board questions from your own case notes.',
        hero: "Your notes from rounds are the best study material you'll ever have. We turn them into questions.",
        stages: [
            {
                label: 'Rounds Prep',
                desc: "Paste your case summaries, H&P notes, or signout sheets and generate clinical reasoning questions. Build pattern recognition from real cases you've personally managed.",
            },
            {
                label: 'USMLE / COMLEX Step 3',
                desc: 'Long-form CCS-style vignettes and clinical management questions from your didactic materials. Covers IM, emergency medicine, pediatrics, ob/gyn, and more in the Step 3 format.',
            },
            {
                label: 'Fellowship & Specialty Boards',
                desc: 'Going for subspecialty certification? Generate questions from your fellowship reading, conference slides, and review articles. Specialty and oral board question formats available.',
            },
        ],
        examTags: ['USMLE Step 3', 'COMLEX L3', 'Rounds Prep', 'Fellowship Boards', 'Specialty Certification'],
        testimonial: {
            quote: "I use FlashFonic after every interesting case. By the time I sat for my boards, I had a Q-bank built entirely from patients I'd personally seen.",
            attribution: 'Daniel Osei · Internal Medicine Resident, PGY-3',
        },
    },
    'np': {
        slug: 'np',
        title: 'Nurse Practitioner',
        subtitle: 'Didactic class prep through FNP and ACNP certification — from your own notes.',
        hero: "Generic Q-banks don't cover your NP curriculum. Yours does.",
        stages: [
            {
                label: 'Didactic Phase',
                desc: 'Paste lecture slides, professor notes, or textbook summaries and get class-focused MCQs. Pathophysiology, pharmacology, and clinical reasoning formatted for NP didactic exams.',
            },
            {
                label: 'Clinical Rotations',
                desc: 'Turn your preceptor notes and clinic summaries into clinical vignette questions. Build the clinical reasoning skills that certification exams actually test.',
            },
            {
                label: 'FNP / ACNP Certification',
                desc: 'Generate board-format questions aligned with AANP and ANCC blueprints from your own review materials. Identify weak areas and drill them systematically.',
            },
        ],
        examTags: ['FNP — AANP', 'FNP — ANCC', 'ACNP', 'Class Prep', 'Rotation Prep'],
        testimonial: {
            quote: "For FNP cert prep, finding practice questions in my specific specialty was nearly impossible. FlashFonic let me build exactly the Q-bank I needed from my own notes.",
            attribution: 'Emily Carter · Family NP Student',
        },
    },
    'crna': {
        slug: 'crna',
        title: 'CRNA',
        subtitle: 'Anesthesia-specific questions no generic Q-bank can match.',
        hero: 'Anesthesia training is unlike anything else. Your Q-bank should be too.',
        stages: [
            {
                label: 'Didactic Year',
                desc: 'Paste pharmacology notes, physiology lectures, and equipment references and get anesthesia-specific MCQs. Covers volatile agents, opioids, regional blocks, and critical care pharmacology.',
            },
            {
                label: 'Clinical Phase',
                desc: 'Turn your case logs and OR notes into clinical reasoning questions. Airway management, hemodynamic scenarios, and anesthetic complications formatted for CRNA clinical thinking.',
            },
            {
                label: 'NBCRNA NCE',
                desc: 'Board-format questions from your NCE review materials. Identify your weak domains — physiology, pharmacology, equipment, professional practice — and build targeted question sets.',
            },
        ],
        examTags: ['NBCRNA NCE', 'Didactic Exams', 'Clinical Competencies'],
        testimonial: {
            quote: "Nothing else generates anesthesia-specific questions at this level. I stopped using generic resources entirely after my first week with FlashFonic.",
            attribution: 'Marcus Bell · CRNA Student, Class of 2026',
        },
    },
    'pa': {
        slug: 'pa',
        title: 'Physician Assistant',
        subtitle: 'Didactic through PANCE and PANRE — from your own study materials.',
        hero: 'Every PA program is different. Now your Q-bank can match your curriculum exactly.',
        stages: [
            {
                label: 'Didactic Phase',
                desc: 'Paste your lecture notes, case studies, and module summaries and get block-exam-ready MCQs. Covers all organ systems in the clinical thinking format PA programs test.',
            },
            {
                label: 'Clinical Year',
                desc: 'Generate vignette questions from your rotation notes. Each rotation — IM, surgery, emergency medicine, peds — gets questions formatted for how that rotation actually tests you.',
            },
            {
                label: 'PANCE & PANRE',
                desc: 'Board-format vignettes from your review materials. FlashFonic tracks your accuracy by organ system and generates focused question sets around your personal weak spots.',
            },
        ],
        examTags: ['PANCE', 'PANRE', 'Didactic Block Exams', 'Clinical Rotation Prep'],
        testimonial: {
            quote: "I failed my first PANCE attempt using generic Q-banks. FlashFonic helped me identify exactly which organ systems I was weakest in. Passed the second time with a full week to spare.",
            attribution: 'Rachel Kim · PA-C, Emergency Medicine',
        },
    },
    'aa': {
        slug: 'aa',
        title: 'Anesthesia Assistant',
        subtitle: 'CAA-specific prep from didactic materials through NCCAA certification.',
        hero: 'Anesthesia Assistant training demands anesthesia-specific questions. Now you can build them.',
        stages: [
            {
                label: 'Didactic Phase',
                desc: 'Paste pharmacology lectures, physiology notes, and clinical guidelines and get AA-program-specific MCQs. Covers the depth and format that CAA training actually demands.',
            },
            {
                label: 'Clinical Phase',
                desc: 'Turn OR case notes and clinical experiences into scenario-based questions. Build the anesthetic reasoning skills that translate directly from OR to exam.',
            },
            {
                label: 'NCCAA Certification',
                desc: 'Board-format questions from your certification review materials. Systematic preparation across every NCCAA content domain.',
            },
        ],
        examTags: ['NCCAA Certification', 'Didactic Exams', 'Clinical Competencies'],
        testimonial: {
            quote: "There's almost nothing out there for AA-specific exam prep. FlashFonic fills that gap completely.",
            attribution: 'Tyler Brooks · Anesthesia Assistant Student, Class of 2026',
        },
    },
    'nursing': {
        slug: 'nursing',
        title: 'Nursing Student',
        subtitle: 'Block exams and NCLEX-style priority action questions from your lecture notes.',
        hero: 'Turn what your professors actually teach into NCLEX-style questions before the exam.',
        stages: [
            {
                label: 'Nursing School Block Exams',
                desc: 'Paste lecture notes, med-surg summaries, and case studies and get NCLEX-style MCQs. Focuses on priority, delegation, and safety — the concepts nursing block exams always emphasize.',
            },
            {
                label: 'Clinical Rotations',
                desc: 'Generate clinical judgment questions from your patient care summaries. Build the clinical reasoning that NCLEX tests through realistic next-action scenarios.',
            },
            {
                label: 'NCLEX-RN & NCLEX-PN',
                desc: 'Board-format questions built from your ATI, Saunders, or lecture materials. FlashFonic generates questions in the NCLEX Clinical Judgment format — not just recall.',
            },
        ],
        examTags: ['NCLEX-RN', 'NCLEX-PN', 'Block Exams', 'Med-Surg', 'Pharmacology'],
        testimonial: {
            quote: "I used FlashFonic to generate questions from every module throughout the semester. By the time I sat for NCLEX, the questions actually felt familiar instead of like a total guessing game.",
            attribution: 'Jasmine Lopez · RN, Med-Surg',
        },
    },
    'allied-health': {
        slug: 'allied-health',
        title: 'Allied Health',
        subtitle: 'Class prep and specialty certification from any allied health program.',
        hero: 'Your field. Your curriculum. Your Q-bank — finally.',
        stages: [
            {
                label: 'Program Coursework',
                desc: 'Paste lecture slides and class notes and get MCQs styled for your specific allied health program. Respiratory therapy, radiology, physical therapy, occupational therapy — every format.',
            },
            {
                label: 'Clinical Placements',
                desc: 'Generate clinical application questions from your fieldwork notes. Reinforce what you learned in practice into exam-ready knowledge.',
            },
            {
                label: 'Certification Exams',
                desc: 'Board-format questions from your review materials. Systematic coverage of your certification content with performance tracking across domains.',
            },
        ],
        examTags: ['NBRC (RT)', 'ARRT (Radiology)', 'NPTE (PT)', 'NBCOT (OT)', 'Certification Exams'],
        testimonial: {
            quote: "As a respiratory therapy student, finding RRT-specific practice questions was a constant struggle. FlashFonic let me build them straight from my class notes.",
            attribution: 'Connor Diaz · Registered Respiratory Therapist Student',
        },
    },
    'clinicians': {
        slug: 'clinicians',
        title: 'Practicing Clinician',
        subtitle: 'Specialty and subspecialty board prep when generic Q-banks fall short.',
        hero: "You've exhausted the question banks. Your own practice is the best source material left.",
        stages: [
            {
                label: 'Specialty Board Certification',
                desc: 'Paste your review articles, society guidelines, and study materials and get specialty-specific board questions. Covers internal medicine subspecialties, surgical specialties, and more — in the exact format your boards use.',
            },
            {
                label: 'Subspecialty & Oral Boards',
                desc: 'Generate scenario-based questions from your fellowship notes and complex case summaries. Oral board format available — clinical reasoning questions styled for viva-style examination.',
            },
            {
                label: 'Maintenance of Certification (MOC)',
                desc: "Stay current with your specialty's evolving guidelines by generating questions from the literature you're already reading. Keep your question bank fresh without manual effort.",
            },
        ],
        examTags: ['ABIM Certification', 'ABFM', 'ABS', 'Subspecialty Boards', 'MOC', 'Oral Boards'],
        testimonial: {
            quote: "When I sat for my ABIM rheumatology boards in 2025, I couldn't find specialty-specific questions that actually reflected what I'd face. I built FlashFonic to fill that gap. Passed with flying colors.",
            attribution: 'Dr. M. Stan, DO · Founder, FlashFonic',
        },
    },
};

const PathwayPage = ({ onEnter, onOpenPlanChooser, onOpenAccount }) => {
    const { pathwaySlug } = useParams();
    const pathway = PATHWAYS[pathwaySlug];

    if (!pathway) {
        return <Navigate to="/" replace />;
    }

    const questionsUsed = getFreeQuestionsUsed();
    const freeRemaining = Math.max(0, FREE_TRIAL_TOTAL - questionsUsed);
    const hasFreeLeft = freeRemaining > 0;

    const handleStart = () => {
        if (typeof onEnter === 'function') onEnter();
    };

    return (
        <div className="min-h-screen text-slate-200" style={{ backgroundColor: '#080d18', fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, sans-serif' }}>

            {/* Background glows */}
            <div aria-hidden="true" className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-96 w-[52rem] rounded-full bg-teal-500/8 blur-3xl" />
                <div className="absolute top-1/2 -right-24 h-80 w-80 rounded-full bg-emerald-500/6 blur-3xl" />
            </div>

            {/* Nav */}
            <nav className="relative z-10 border-b border-white/5 bg-black/20 backdrop-blur-sm">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 flex items-center justify-between gap-2 py-3">
                    <a href="/" className="flex items-center gap-2 shrink-0 min-w-0">
                        <div className="leading-none">
                            <p className="text-2xl sm:text-[28px] font-semibold tracking-tight">
                                <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">FlashFonic</span>
                                <span className="align-top text-teal-400/70 text-xs">™</span>
                            </p>
                            <p className="text-[11px] sm:text-xs text-slate-400 mt-0.5 hidden sm:block">Listen. Flash it. Learn.</p>
                        </div>
                    </a>
                    <div className="flex items-center gap-3">
                        <a href="/" className="text-xs text-slate-500 hover:text-slate-300 transition hidden sm:inline">
                            ← All pathways
                        </a>
                        <button onClick={onOpenAccount} className="text-sm text-slate-400 hover:text-white transition">
                            Sign in
                        </button>
                        <button
                            onClick={handleStart}
                            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:opacity-90 px-4 py-1.5 text-sm font-medium text-white transition shadow-lg shadow-teal-500/20"
                        >
                            Get Started
                        </button>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section className="relative z-10 pt-16 pb-12 px-4 sm:px-6">
                <div className="mx-auto max-w-3xl">
                    <a href="/" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition mb-6 sm:hidden">
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        All pathways
                    </a>
                    <div className="inline-flex items-center gap-2 rounded-full border border-teal-400/25 bg-teal-500/10 px-4 py-1.5 text-xs font-medium text-teal-300 mb-6">
                        {pathway.title}
                    </div>
                    <h1 className="text-3xl sm:text-5xl font-bold tracking-tight text-white leading-[1.1] mb-4">
                        {pathway.hero}
                    </h1>
                    <p className="text-base text-slate-400 leading-relaxed mb-8">{pathway.subtitle}</p>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        <button
                            onClick={handleStart}
                            className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 px-8 py-3.5 text-sm font-semibold text-white shadow-xl shadow-teal-500/25 transition"
                        >
                            {hasFreeLeft ? `Start free — ${freeRemaining} questions included` : 'Get started'}
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14M12 5l7 7-7 7" />
                            </svg>
                        </button>
                        {hasFreeLeft && (
                            <p className="text-xs text-slate-600">No credit card required</p>
                        )}
                    </div>

                    {/* Exam tags */}
                    <div className="flex flex-wrap gap-2 mt-8">
                        {pathway.examTags.map(tag => (
                            <span key={tag} className="inline-block rounded-full border border-teal-400/20 bg-teal-500/8 px-3 py-1 text-xs font-medium text-teal-300">
                                {tag}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* Stages */}
            <section className="relative z-10 py-12 px-4 sm:px-6 border-t border-white/5">
                <div className="mx-auto max-w-3xl">
                    <h2 className="text-lg font-bold text-white mb-8">Prep for every stage</h2>
                    <div className="flex flex-col gap-4">
                        {pathway.stages.map((stage, i) => (
                            <div key={i} className="rounded-2xl border border-white/8 p-6 flex gap-5" style={{ background: 'rgba(255,255,255,0.02)' }}>
                                <div className="shrink-0 flex h-8 w-8 items-center justify-center rounded-xl bg-teal-500/10 border border-teal-400/20 text-xs font-bold text-teal-300">
                                    {i + 1}
                                </div>
                                <div className="min-w-0">
                                    <h3 className="text-sm font-semibold text-white mb-1.5">{stage.label}</h3>
                                    <p className="text-xs text-slate-400 leading-relaxed">{stage.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonial */}
            <section className="relative z-10 py-12 px-4 sm:px-6 border-t border-white/5">
                <div className="mx-auto max-w-3xl">
                    <div className="rounded-2xl border border-white/8 bg-teal-500/4 p-6">
                        <svg className="h-5 w-5 text-teal-400/40 mb-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                        </svg>
                        <p className="text-base text-slate-200 leading-relaxed italic">&ldquo;{pathway.testimonial.quote}&rdquo;</p>
                        <p className="text-xs font-medium text-slate-500 mt-4 border-t border-white/8 pt-3">{pathway.testimonial.attribution}</p>
                    </div>
                </div>
            </section>

            {/* Pricing CTA */}
            <section className="relative z-10 py-12 px-4 sm:px-6 border-t border-white/5">
                <div className="mx-auto max-w-3xl text-center">
                    <h2 className="text-xl font-bold text-white mb-2">Ready to build your Q-bank?</h2>
                    <p className="text-sm text-slate-400 mb-8">
                        {hasFreeLeft
                            ? `Start free with ${freeRemaining} questions — no credit card required.`
                            : 'Choose a plan that fits your training stage.'}
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <button
                            onClick={handleStart}
                            className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 px-8 py-3.5 text-sm font-semibold text-white shadow-xl shadow-teal-500/25 transition"
                        >
                            {hasFreeLeft ? 'Start free' : 'Get started'}
                        </button>
                        <button
                            onClick={() => onOpenPlanChooser?.({ isClinicals: true })}
                            className="inline-flex items-center rounded-2xl border border-white/12 px-6 py-3.5 text-sm text-slate-300 hover:border-white/25 hover:text-white transition"
                        >
                            View pricing
                        </button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="relative z-10 py-8 px-4 sm:px-6 border-t border-white/5">
                <div className="mx-auto max-w-5xl flex flex-col sm:flex-row items-center justify-between gap-2">
                    <a href="/" className="text-2xl sm:text-[28px] font-semibold tracking-tight">
                        <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">FlashFonic</span>
                        <span className="align-top text-teal-400/70 text-xs">™</span>
                    </a>
                    <div className="flex items-center gap-4 text-xs text-slate-600">
                        <a href="/terms" className="hover:text-white transition">Terms</a>
                        <a href="/privacy" className="hover:text-white transition">Privacy</a>
                        <a href="mailto:support@flashfonic.com" className="hover:text-white transition">Contact</a>
                    </div>
                    <p className="text-xs text-slate-700">&copy; FlashFonic, Trifecta Pro LLC</p>
                </div>
            </footer>
        </div>
    );
};

export default PathwayPage;
